import { createContext, useCallback, useContext, useState } from 'react';
import { CheckCircle, Warning, X } from 'phosphor-react';

type ToastVariant = 'success' | 'error';

interface ToastItem {
  id: number;
  message: string;
  variant: ToastVariant;
}

interface ToastContextValue {
  showToast: (message: string, variant?: ToastVariant) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
};

let nextId = 0;

export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const removeToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string, variant: ToastVariant = 'success') => {
      const id = nextId++;
      setToasts(prev => [...prev, { id, message, variant }]);
      setTimeout(() => removeToast(id), 3500);
    },
    [removeToast],
  );

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="pointer-events-none fixed top-6 left-1/2 z-[100] flex -translate-x-1/2 flex-col items-center gap-3">
        {toasts.map(toast => (
          <div
            key={toast.id}
            className={`pointer-events-auto flex items-center gap-3 rounded-2xl border-2 px-5 py-4 text-sm font-semibold shadow-2xl backdrop-blur-xl animate-toast-in ${
              toast.variant === 'error'
                ? 'border-red-400/60 bg-red-500/30 text-white ring-1 ring-red-400/30'
                : 'border-brand-400/60 bg-brand-500/30 text-white ring-1 ring-brand-400/30'
            }`}
          >
            {toast.variant === 'error' ? (
              <Warning size={22} weight="fill" />
            ) : (
              <CheckCircle size={22} weight="fill" />
            )}
            <span>{toast.message}</span>
            <button
              type="button"
              onClick={() => removeToast(toast.id)}
              className="ml-1 text-white/60 transition hover:text-white"
            >
              <X size={16} weight="bold" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};
